import { Plan, PlanFeatureFlags } from './models';

// Single source of plan display copy (landing parity).
//
// Backend C1 `description` / `featuresDisplay` are PRIMARY at every call
// site; the tier maps below are the fallback for backends that omit display
// fields. Copy is keyed by stable C1 `tier` (plus legacy name aliases),
// never by plan id heuristics. Prices always come from the payload:
// mensual shows the raw amount, anual shows the monthly equivalent
// (total / 12) — the same math as the landing toggle.

export const TIER_TAGLINES: { [key: string]: string } = {
  emprendedor: 'Ideal para pequeños cafés, almacenes o un solo televisor.',
  comercial: 'Perfecto para restaurantes con menú boards de varias pantallas.',
  corporativo: 'Para cadenas de locales de comida o tiendas comerciales.',
  franquicia: 'Para redes masivas de franquicias o grandes centros de compras.'
};

export const TIER_ALIASES: { [key: string]: string } = {
  basico: 'emprendedor',
  estandar: 'comercial',
  premium: 'corporativo'
};

export const TIER_FEATURES: { [key: string]: { text: string; included: boolean }[] } = {
  emprendedor: [
    { text: '1 Pantalla activa', included: true },
    { text: 'App oficial para Google TV', included: true },
    { text: 'Sube imágenes y videos', included: true },
    { text: 'Sincronización Multicaja', included: false }
  ],
  comercial: [
    { text: 'Hasta 3 Pantallas', included: true },
    { text: 'Sincronización Multicaja', included: true },
    { text: 'Reels programados por hora', included: true },
    { text: 'Zócalos de ofertas flash', included: true }
  ],
  corporativo: [
    { text: 'Hasta 10 Pantallas', included: true },
    { text: 'Monitoreo visual en vivo', included: true },
    { text: 'Planificación de ofertas semanales', included: true },
    { text: 'Soporte prioritario 24/7', included: true }
  ],
  franquicia: [
    { text: 'Pantallas ilimitadas', included: true },
    { text: 'Marca blanca para tu empresa', included: true },
    { text: 'Despliegue On-Premise', included: true },
    { text: 'API de integración de stock', included: true }
  ]
};

// Machine flag fallback mirror of backend TIERS (gates only, c1-gates).
// Real C3 sends no feature flags; when flags are absent but the tier is a
// known canonical tier, gates resolve from here. Closed-world: every known
// tier states every gated flag explicitly (absent key would fail-open).
// Real flags always win when present; unknown tiers stay fail-open.
export const TIER_FLAGS: { [key: string]: PlanFeatureFlags } = {
  emprendedor: { multicaja: false, franjasHorarias: false, zocalos: false, monitoreo: false },
  comercial: { multicaja: true, franjasHorarias: true, zocalos: true, monitoreo: false },
  corporativo: { multicaja: true, franjasHorarias: true, zocalos: true, monitoreo: true },
  franquicia: { multicaja: true, franjasHorarias: true, zocalos: true, monitoreo: true }
};

// Resolves the canonical tier from the C1 payload (tier first, display
// name as fallback, legacy mock vocabularies aliased). Returns '' when
// nothing matches so callers can fall back to payload-derived copy.
export function canonicalTier(plan: Plan): string {
  if (!plan) { return ''; }
  var tier: string = String((plan as any).tier || '').toLowerCase();
  if (TIER_TAGLINES[tier]) { return tier; }
  if (TIER_ALIASES[tier]) { return TIER_ALIASES[tier]; }
  var name: string = String((plan as any).planName || (plan as any).name || '').toLowerCase();
  if (TIER_TAGLINES[name]) { return name; }
  if (TIER_ALIASES[name]) { return TIER_ALIASES[name]; }
  return '';
}

// Trimmed backend `description` or '' (never fabricates copy).
export function payloadDescription(plan: Plan): string {
  var raw: any = plan ? (plan as any).description : null;
  if (typeof raw === 'string' && raw.trim().length > 0) { return raw.trim(); }
  return '';
}

// Trimmed backend `featuresDisplay` items or [] (never fabricates copy).
export function payloadFeatures(plan: Plan): string[] {
  var display: any = plan ? (plan as any).featuresDisplay : null;
  if (!Array.isArray(display)) { return []; }
  return display.filter(function(item: any) {
    return typeof item === 'string' && item.trim().length > 0;
  }).map(function(item: string) { return item.trim(); });
}

// Neutral payload-derived description (maxScreens) for surfaces without
// tier copy. No marketing claims, so frozen C1 stays intact.
export function describeFromPayload(plan: Plan): string {
  if (!plan) { return ''; }
  var max: any = (plan as any).maxScreens;
  if (typeof max === 'number' && isFinite(max) && max > 0) {
    return max === 1 ? 'Incluye 1 pantalla activa' : 'Incluye hasta ' + max + ' pantallas activas';
  }
  return 'Plan de señalización digital';
}

// Card description: backend copy first, tier copy second, `fallback` last.
export function taglineFor(plan: Plan, fallback: string): string {
  var raw: string = payloadDescription(plan);
  if (raw) { return raw; }
  var tier: string = canonicalTier(plan);
  if (tier && TIER_TAGLINES[tier]) { return TIER_TAGLINES[tier]; }
  return fallback || '';
}

// Card feature list: backend copy first (all included), tier copy second,
// null when neither exists so callers can apply their own last resort.
export function featuresFor(plan: Plan): { text: string; included: boolean }[] | null {
  var items: string[] = payloadFeatures(plan);
  if (items.length > 0) {
    return items.map(function(text: string) { return { text: text, included: true }; });
  }
  var tier: string = canonicalTier(plan);
  if (tier && TIER_FEATURES[tier]) { return TIER_FEATURES[tier]; }
  return null;
}

// Trims payload amounts for display ('9.00' -> '9', '19.99' stays).
export function formatPayloadAmount(raw: any): string {
  var n: number = Number(raw);
  if (!isFinite(n)) { return '0'; }
  return String(parseFloat(n.toFixed(2)));
}

// Big card price from the payload only: raw amount when mensual, monthly
// equivalent (total / 12) when anual. Charging always uses the raw total.
export function priceDisplay(plan: Plan): string {
  if (!plan) { return '0'; }
  var billing: string = (plan as any).billing || (plan as any).billingCycle || 'mensual';
  if (billing === 'anual') {
    var n: number = Number((plan as any).amount);
    if (!isFinite(n)) { return '0'; }
    return formatPayloadAmount(n / 12);
  }
  return formatPayloadAmount((plan as any).amount);
}

// Raw anual total for the 'Facturado anual' line (payload only).
export function annualTotal(plan: Plan): string {
  if (!plan) { return '0'; }
  return formatPayloadAmount((plan as any).amount);
}



// WEBPACK FOOTER //
// ./src/app/providers/plan-display.ts